import { useRef, useEffect, useCallback } from 'react';
import { useWebSocket } from '@/hooks/useWebSocket';
import type { LogLevel } from '@/types';

const levelColors: Record<LogLevel, string> = {
  info: 'text-accent-blue',
  warn: 'text-accent-yellow',
  error: 'text-accent-red',
  debug: 'text-text-muted',
  query: 'text-accent-purple',
};

function formatTimestamp(ts: string): string {
  try {
    const d = new Date(ts);
    return d.toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' });
  } catch {
    return ts;
  }
}

interface LogViewProps {
  onClearRef?: React.MutableRefObject<(() => void) | null>;
}

export function LogView({ onClearRef }: LogViewProps) {
  const { logs, clearLogs } = useWebSocket();
  const containerRef = useRef<HTMLDivElement>(null);
  const isScrollLockedRef = useRef(true);

  // Expose clearLogs to parent via ref
  useEffect(() => {
    if (onClearRef) {
      onClearRef.current = clearLogs;
    }
    return () => {
      if (onClearRef) {
        onClearRef.current = null;
      }
    };
  }, [clearLogs, onClearRef]);

  // Auto-scroll to bottom when new entries arrive, unless user scrolled up
  useEffect(() => {
    const el = containerRef.current;
    if (el && isScrollLockedRef.current) {
      el.scrollTop = el.scrollHeight;
    }
  }, [logs]);

  const handleScroll = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    // Lock if scrolled within 20px of bottom
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 20;
    isScrollLockedRef.current = atBottom;
  }, []);

  return (
    <div
      ref={containerRef}
      className="h-full overflow-y-auto font-mono"
      onScroll={handleScroll}
    >
      {logs.length === 0 && (
        <div className="flex items-center justify-center h-full text-text-muted text-xs">
          No log entries yet
        </div>
      )}
      {logs.map((entry, i) => (
        <div
          key={`${entry.timestamp}-${i}`}
          className="flex items-baseline py-[1px] px-3 hover:bg-dbgo-tab-hover transition-colors"
        >
          {/* Timestamp */}
          <span className="w-[70px] flex-shrink-0 text-[11.5px] text-text-muted">
            {formatTimestamp(entry.timestamp)}
          </span>

          {/* Level badge */}
          <span
            className={`w-[42px] flex-shrink-0 uppercase text-[10px] font-semibold ${levelColors[entry.level]}`}
          >
            {entry.level}
          </span>

          {/* Message + duration */}
          <span className="text-text-secondary text-xs min-w-0">
            {entry.message}
            {entry.durationMs != null && (
              <span className="text-accent-green ml-1.5">
                {entry.durationMs}ms
              </span>
            )}
          </span>
        </div>
      ))}
    </div>
  );
}
